import {
  and,
  asc,
  asset,
  attachment,
  desc,
  eq,
  isNotNull,
  type PowerSyncDrizzleDB,
  reminder_rule,
  service_record,
  sql,
  usage_reading,
  warranty,
} from "@tendly/powersync";
import {
  liveAssetFilters,
  liveReminderRuleFilters,
  liveServiceRecordFilters,
  liveWarrantyFilters,
} from "../live/filters";

/**
 * Tendly domain query builders. All operate on local PowerSync SQLite —
 * zero network. Costs in minor units (cents, integers);
 * usage readings in integers (km/hours).
 *
 * SOFT-DELETE: the invariant "live row = deleted_at IS NULL" (+ cohort
 * parent-asset-not-in-trash for children) lives centralized in the live layer
 * (`../live/filters`). Here it is only COMPOSED. The only reads that see
 * deleted rows are the trash (`getTrashedAssetsQuery`) and the id-scoped lookup
 * (`getAssetDetailQuery`), both declared in guard-manifest.
 */

// ─── Assets ───────────────────────────────────────────────────────────────────

export function getAssetsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(asset)
    .where(and(eq(asset.user_id, userId), ...liveAssetFilters()))
    .orderBy(asc(asset.sort_order), asc(asset.created_at));
}

export type AssetWithMeta = Awaited<
  ReturnType<ReturnType<typeof getAssetsQuery>["execute"]>
>[number];

export function getAssetDetailQuery(db: PowerSyncDrizzleDB, assetId: string) {
  return db.select().from(asset).where(eq(asset.id, assetId));
}

/** Trashed assets (soft-delete), newest first. */
export function getTrashedAssetsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(asset)
    .where(and(eq(asset.user_id, userId), isNotNull(asset.deleted_at)))
    .orderBy(desc(asset.deleted_at));
}

// ─── Services ─────────────────────────────────────────────────────────────────

export function getServiceRecordsQuery(db: PowerSyncDrizzleDB, assetId: string) {
  return db
    .select()
    .from(service_record)
    .where(and(eq(service_record.asset_id, assetId), ...liveServiceRecordFilters()))
    .orderBy(desc(service_record.performed_at));
}

export type ServiceRecordWithMeta = Awaited<
  ReturnType<ReturnType<typeof getServiceRecordsQuery>["execute"]>
>[number];

/**
 * All user services (analytics / reports view). Returns only what is
 * necessary for aggregations (cost, date, category, asset) to keep payload light.
 *
 * COHORT BUG (fixed upon centralization): previously filtered ONLY
 * `isNull(service_record.deleted_at)`, so costs of services whose
 * ASSETS were in trash continued summing up in analytics/reports.
 * `liveServiceRecordFilters()` adds cohort (parent asset not in trash) and
 * excludes them — consistent with rest of views.
 */
export function getAllServiceRecordsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: service_record.id,
      asset_id: service_record.asset_id,
      title: service_record.title,
      category: service_record.category,
      cost_cents: service_record.cost_cents,
      currency: service_record.currency,
      performed_at: service_record.performed_at,
    })
    .from(service_record)
    .where(and(eq(service_record.user_id, userId), ...liveServiceRecordFilters()))
    .orderBy(desc(service_record.performed_at));
}

export interface ServiceRecordLite {
  id: string;
  asset_id: string | null;
  title: string | null;
  category: string | null;
  cost_cents: number | null;
  currency: string | null;
  performed_at: string | null;
}

/**
 * Annual costs per asset: yearly total and breakdown by category.
 * `performed_at` is stored as ISO-8601 → strftime('%Y') extracts the year.
 */
export function getAnnualCostsQuery(db: PowerSyncDrizzleDB, assetId: string) {
  return db
    .select({
      year: sql<string>`strftime('%Y', ${service_record.performed_at})`.as("year"),
      category: service_record.category,
      total_cents: sql<number>`COALESCE(SUM(${service_record.cost_cents}), 0)`.as("total_cents"),
      count: sql<number>`COUNT(*)`.as("count"),
    })
    .from(service_record)
    .where(and(eq(service_record.asset_id, assetId), ...liveServiceRecordFilters()))
    .groupBy(sql`strftime('%Y', ${service_record.performed_at})`, service_record.category)
    .orderBy(desc(sql`strftime('%Y', ${service_record.performed_at})`));
}

export interface AnnualCostRow {
  year: string | null;
  category: string | null;
  total_cents: number;
  count: number;
}

// ─── Usage readings ──────────────────────────────────────────────────────────

export function getUsageReadingsQuery(db: PowerSyncDrizzleDB, assetId: string) {
  return db
    .select()
    .from(usage_reading)
    .where(eq(usage_reading.asset_id, assetId))
    .orderBy(desc(usage_reading.recorded_at));
}

export type UsageReadingWithMeta = Awaited<
  ReturnType<ReturnType<typeof getUsageReadingsQuery>["execute"]>
>[number];

/** All user readings (for projections in "upcoming"). */
export function getAllUsageReadingsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      asset_id: usage_reading.asset_id,
      value: usage_reading.value,
      recorded_at: usage_reading.recorded_at,
    })
    .from(usage_reading)
    .where(eq(usage_reading.user_id, userId))
    .orderBy(asc(usage_reading.recorded_at));
}

export interface UsageReadingLite {
  asset_id: string | null;
  value: number | null;
  recorded_at: string | null;
}

// ─── Reminder rules ───────────────────────────────────────────────────

/** Active user rules with asset metadata ("upcoming" view). */
export function getReminderRulesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: reminder_rule.id,
      asset_id: reminder_rule.asset_id,
      title: reminder_rule.title,
      category: reminder_rule.category,
      interval_months: reminder_rule.interval_months,
      interval_usage: reminder_rule.interval_usage,
      last_done_at: reminder_rule.last_done_at,
      last_done_usage: reminder_rule.last_done_usage,
      lead_days: reminder_rule.lead_days,
      active: reminder_rule.active,
      note: reminder_rule.note,
      asset_name: asset.name,
      asset_kind: asset.kind,
      asset_usage_unit: asset.usage_unit,
    })
    .from(reminder_rule)
    .innerJoin(asset, eq(reminder_rule.asset_id, asset.id))
    .where(and(eq(reminder_rule.user_id, userId), ...liveReminderRuleFilters()))
    .orderBy(asc(reminder_rule.created_at));
}

export type ReminderRuleWithAsset = Awaited<
  ReturnType<ReturnType<typeof getReminderRulesQuery>["execute"]>
>[number];

// ─── Warranties ───────────────────────────────────────────────────────────────

/** User warranties with asset metadata. */
export function getWarrantiesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: warranty.id,
      asset_id: warranty.asset_id,
      title: warranty.title,
      purchase_date: warranty.purchase_date,
      duration_months: warranty.duration_months,
      expires_at: warranty.expires_at,
      vendor: warranty.vendor,
      note: warranty.note,
      asset_name: asset.name,
      asset_kind: asset.kind,
    })
    .from(warranty)
    .innerJoin(asset, eq(warranty.asset_id, asset.id))
    .where(and(eq(warranty.user_id, userId), ...liveWarrantyFilters()))
    .orderBy(asc(warranty.expires_at));
}

export type WarrantyWithAsset = Awaited<
  ReturnType<ReturnType<typeof getWarrantiesQuery>["execute"]>
>[number];

// ─── Documents (attachments) ─────────────────────────────────────────────────

/** Asset documents (includes invoices for its services and warranties). */
export function getAssetAttachmentsQuery(db: PowerSyncDrizzleDB, assetId: string) {
  return db
    .select()
    .from(attachment)
    .where(eq(attachment.asset_id, assetId))
    .orderBy(desc(attachment.created_at));
}

export function getServiceAttachmentsQuery(db: PowerSyncDrizzleDB, serviceRecordId: string) {
  return db
    .select()
    .from(attachment)
    .where(eq(attachment.service_record_id, serviceRecordId))
    .orderBy(desc(attachment.created_at));
}
